import { useEffect, useRef, useState } from "preact/hooks";
import { locale, tr } from "../i18n.js";
import { del, fmtBytes, get, html, patch, post, put, readFileBase64 } from "../lib.js";
import { Badge, Empty, Field, Loading, Modal, PageHeader, toast, useAction, useForm, useLoad } from "../ui.js";
import { PageGuide } from "../guide.js";
import { openAssistant } from "../assistant.js";
import { hasPlaceholder } from "../placeholders.js";

const MODELS = [
  { id: "claude-opus-5-5", label: `Claude Opus 5.5: ${tr("best judgment for sales and edge cases")}` },
  { id: "claude-sonnet-5-5", label: `Claude Sonnet 5.5: ${tr("fast and capable, lower cost")}` },
  { id: "claude-haiku-5-5", label: `Claude Haiku 5.5: ${tr("lowest cost, simple FAQ and booking")}` },
];

const HOURS = Array.from({ length: 24 }, (_, h) => html`<option value=${h}>${String(h).padStart(2, "0")}:00</option>`);

/**
 * The form is keyed on `updated_at`, so a change applied from the assistant remounts it with the
 * saved values: a stale field would otherwise put the old value back on the next Save.
 */
export function AgentPage({ t }) {
  const [cfg, reload] = useLoad(() => get(`${t.base}/agent`), [t.id]);
  const [kbTick, setKbTick] = useState(0);
  useEffect(() => {
    const onApplied = () => {
      setKbTick((n) => n + 1);
      reload();
    };
    window.addEventListener("rz:config-applied", onApplied);
    return () => window.removeEventListener("rz:config-applied", onApplied);
  }, [t.id]);
  return html`<div>
    <${PageHeader} title=${tr("AI agent")} subtitle=${tr("How your agent talks, what it knows, and when it reaches out")} />
    <${PageGuide} id="agent" />
    ${t.role !== "agent" &&
    html`<div class="callout assistant-callout">
      <div>
        <strong>${tr("Update by chat")}</strong>
        <p class="muted small">${tr("Say what you want changed, in your own words. The assistant writes the setting and shows it to you; nothing changes until you press Apply.")}</p>
      </div>
      <button class="btn small" onClick=${() => openAssistant()}>${tr("Open the assistant")}</button>
    </div>`}
    <div class="grid-agent">
      <div>
        ${cfg ? html`<${AgentForm} key=${cfg.updated_at} t=${t} cfg=${cfg} onSaved=${reload} />` : html`<${Loading} />`}
        <${AiKey} t=${t} />
        <${WeeklyReview} t=${t} onApplied=${() => { setKbTick((n) => n + 1); reload(); }} />
        <${Knowledge} t=${t} tick=${kbTick} />
        <${Documents} t=${t} />
      </div>
      <${TestChat} t=${t} />
    </div>
  </div>`;
}

/**
 * The standard reminder template RELAYZA creates in the business's WhatsApp account. Reminders
 * for bookings made more than a day ahead need one; without it they only reached customers who
 * had written in the last 24 hours.
 */
function ReminderTemplate({ t }) {
  const [state, reload] = useLoad(() => get(`${t.base}/agent/reminder-template`), [t.id]);
  const [busy, run] = useAction();
  if (!state?.whatsapp || state.custom) return null;
  const create = async () => {
    await run(() => post(`${t.base}/agent/reminder-template`), tr("Sent to Meta"));
    reload();
  };
  const line = !state.can_create
    ? tr("Your WhatsApp number was added by hand, so the reminder template can't be created for you. Reconnect it with Connect WhatsApp, or create a template in Meta's WhatsApp Manager and enter its name above.")
    : state.status === "approved"
      ? tr("Reminder template approved by Meta and in use: reminders reach customers whenever they last wrote.")
      : state.status === "pending"
        ? tr("Reminder template sent to Meta for approval. It usually takes a few minutes; reminders use it as soon as it is approved.")
        : state.status === "rejected"
          ? tr("Meta rejected the reminder template: {reason}", { reason: state.error ?? "" })
          : state.status === "failed"
            ? tr("Couldn't create the reminder template: {reason}", { reason: state.error ?? "" })
            : tr("The reminder template hasn't been created yet. It is created for you within the hour, or now:");
  return html`<div class="callout small">
    ${line}
    ${state.can_create && state.status !== "approved" && html` <button type="button" class="btn small" disabled=${busy} onClick=${create}>${state.status === "pending" ? tr("Check again") : tr("Create it now")}</button>`}
  </div>`;
}

function AgentForm({ t, cfg, onSaved }) {
  const { values, bind, check } = useForm({ ...cfg, reminder_offsets: cfg.reminder_offsets.map((m) => m / 60).join(", ") });
  const [busy, run] = useAction();
  const submit = async (e) => {
    e.preventDefault();
    const offsets = String(values.reminder_offsets)
      .split(",")
      .map((s) => Math.round(Number(s.trim()) * 60))
      .filter((n) => n > 0);
    const { tenant_id, updated_at, ...rest } = values;
    await run(
      () =>
        patch(`${t.base}/agent`, {
          ...rest,
          reminder_offsets: offsets,
          followup_delay_hours: Number(values.followup_delay_hours),
          followup_max: Number(values.followup_max),
          human_return_hours: Number(values.human_return_hours),
          quiet_start_hour: Number(values.quiet_start_hour),
          quiet_end_hour: Number(values.quiet_end_hour),
          daily_send_cap: Number(values.daily_send_cap),
          review_delay_hours: Number(values.review_delay_hours),
          nps_delay_hours: Number(values.nps_delay_hours),
        }),
      tr("Agent updated"),
    );
    onSaved();
  };
  return html`<form class="card" onSubmit=${submit}>
    <div class="section-head"><h2>${tr("Persona")}</h2><label class="toggle"><input type="checkbox" ...${check("enabled")} /> ${tr("Agent is answering customers")}</label></div>
    <${Field} label=${tr("How replies go out")} hint=${values.reply_mode === "draft"
      ? tr("Each reply waits in the Inbox for a person to send or discard, with any booking, change or cancellation the agent wants to make. Nothing reaches the customer until then. The test chat on this page still answers directly.")
      : tr("The agent answers customers by itself, around the clock.")}>
      <select ...${bind("reply_mode")}>
        <option value="auto">${tr("Send automatically")}</option>
        <option value="draft">${tr("Hold for approval (draft)")}</option>
      </select>
    </${Field}>
    <div class="row">
      <${Field} label=${tr("Agent name")}><input required ...${bind("agent_name")} /></${Field}>
      <${Field} label=${tr("Default language")}><input ...${bind("default_language")} /></${Field}>
    </div>
    <${Field} label=${tr("Tone of voice")} hint=${tr("e.g. warm and professional, short sentences, a little humor")}><input ...${bind("tone")} /></${Field}>
    <${Field} label=${tr("About the business")} hint=${tr("What you do, who you serve, what makes you different.")}><textarea rows="4" ...${bind("business_description")}></textarea></${Field}>
    <${Field} label=${tr("Sales script and policies")} hint=${tr("How to qualify leads, what to recommend, cancellation policy, deposits, anything the agent must always or never do.")}><textarea rows="6" ...${bind("instructions")}></textarea></${Field}>
    <${Field} label=${tr("Also hand off to a person when…")} hint=${tr("One rule per line, e.g. 'the customer asks about group events'")}><textarea rows="3" ...${bind("handoff_rules")}></textarea></${Field}>
    <${Field} label=${tr("Give a chat back to the AI after a person's reply (hours)")} hint=${tr("When someone on the team answers, here or from the WhatsApp Business app, the AI steps aside. It takes the chat back this many hours after their last reply, if the customer hasn't written since. 0 keeps the chat with the team until someone presses Close.")}><input type="number" min="0" max="168" ...${bind("human_return_hours")} /></${Field}>
    <div class="row">
      <${Field} label=${tr("Model")}><select ...${bind("model")}>${MODELS.map((m) => html`<option value=${m.id}>${m.label}</option>`)}</select></${Field}>
      <${Field} label=${tr("Effort")} hint=${tr("Higher thinks longer before answering")}><select ...${bind("effort")}><option value="low">${tr("Low (fastest)")}</option><option value="medium">${tr("Medium")}</option><option value="high">${tr("High")}</option></select></${Field}>
    </div>

    <h2>${tr("Reminders")}</h2>
    <${Field} label=${tr("Send reminders this many hours before")} hint=${tr("Comma-separated, e.g. 24, 2")}><input ...${bind("reminder_offsets")} /></${Field}>
    <div class="row">
      <${Field} label=${tr("WhatsApp reminder template")} hint=${tr("Approved template name with body variables {{1}} name, {{2}} service, {{3}} time. Needed when the customer hasn't written in 24h.")}><input ...${bind("reminder_template")} /></${Field}>
      <${Field} label=${tr("Template language")}><input ...${bind("reminder_template_lang")} /></${Field}>
    </div>
    <${ReminderTemplate} t=${t} />
    <p class="muted small">${tr("A customer the chat can't reach (website chat, or no template yet) gets the reminder on WhatsApp at the mobile number they gave, or else by email.")}${t.sms_available ? ` ${tr("With neither, it goes by SMS to their mobile.")}` : ""}</p>

    <h2>${tr("Follow-ups")}</h2>
    <label class="toggle"><input type="checkbox" ...${check("followup_enabled")} /> ${tr("Follow up with leads who stop replying before booking")}</label>
    <div class="row">
      <${Field} label=${tr("After (hours)")}><input type="number" min="1" max="23" ...${bind("followup_delay_hours")} /></${Field}>
      <${Field} label=${tr("Max follow-ups")}><input type="number" min="0" max="3" ...${bind("followup_max")} /></${Field}>
    </div>
    <${Field} label=${tr("Follow-up message")} hint=${tr("Leave empty for the default. {name} is replaced with the customer's name.")}><textarea rows="2" ...${bind("followup_message")}></textarea></${Field}>
    <${Field} label=${tr("WhatsApp follow-up template")} hint=${tr("Used if the 24h window has closed.")}><input ...${bind("followup_template")} /></${Field}>

    <h2 id="aftercare">${tr("After the visit")}</h2>
    <p class="muted small">${tr("Starts when you mark an appointment")} <strong>${tr("Attended")}</strong> ${tr("on the Bookings page. Customers who opted out get nothing.")}</p>
    <label class="toggle"><input type="checkbox" ...${check("review_enabled")} /> ${tr("Ask for a review after the visit")}</label>
    <div class="row">
      <${Field} label=${tr("Review link")} hint=${tr("Your Google review link (Business Profile → Ask for reviews), or any https:// page.")}><input type="url" dir="ltr" placeholder="https://g.page/r/…/review" ...${bind("review_url")} /></${Field}>
      <${Field} label=${tr("Hours after the visit")}><input type="number" min="0" max="72" ...${bind("review_delay_hours")} /></${Field}>
    </div>
    <${Field} label=${tr("Review message")} hint=${tr("Leave empty for the default. {name}, {business} and {link} are filled in. Ask every customer the same way: asking only happy customers breaks Google's review rules.")}><textarea rows="2" dir="auto" ...${bind("review_message")}></textarea></${Field}>
    <${Field} label=${tr("WhatsApp review template")} hint=${tr("Body variables {{1}} name, {{2}} review link. Needed when the customer hasn't written in 24h.")}><input ...${bind("review_template")} /></${Field}>
    <label class="toggle"><input type="checkbox" ...${check("nps_enabled")} /> ${tr("Ask how likely they are to recommend you (0 to 10)")}</label>
    <p class="muted small">${tr("A low score (0 to 6) hands the chat to your team with an alert and a call-back task, so you reach an unhappy customer first. It doesn't change who gets the review request: that still goes to everyone.")}</p>
    <div class="row">
      <${Field} label=${tr("Hours after the visit")}><input type="number" min="0" max="72" ...${bind("nps_delay_hours")} /></${Field}>
      <${Field} label=${tr("WhatsApp score template")} hint=${tr("Body variable {{1}} name. Needed when the customer hasn't written in 24h.")}><input ...${bind("nps_template")} /></${Field}>
    </div>
    <${Field} label=${tr("Score question")} hint=${tr("Leave empty for the default. {name} and {business} are filled in. Ask for a number from 0 to 10, so the reply can be read.")}><textarea rows="2" dir="auto" ...${bind("nps_message")}></textarea></${Field}>
    <label class="toggle"><input type="checkbox" ...${check("rebook_enabled")} /> ${tr("Invite customers to book again")}</label>
    <p class="muted small">${tr("Sent after the interval you set per service under Services & staff, unless the customer already booked again.")}</p>
    <${Field} label=${tr("Rebook message")} hint=${tr("Leave empty for the default. {name}, {service} and {business} are filled in. The AI agent handles the reply and books.")}><textarea rows="2" dir="auto" ...${bind("rebook_message")}></textarea></${Field}>
    <${Field} label=${tr("WhatsApp rebook template")} hint=${tr("Body variables {{1}} name, {{2}} service.")}><input ...${bind("rebook_template")} /></${Field}>

    <h2 id="waitlist">${tr("Waitlist")}</h2>
    <label class="toggle"><input type="checkbox" ...${check("waitlist_enabled")} /> ${tr("Offer a waitlist when no suitable time is free")}</label>
    <p class="muted small">${tr("When a booking is cancelled or moved, the freed time is offered to up to 2 matching customers, longest waiting first. Whoever confirms first gets it; the agent books it.")}</p>
    <${Field} label=${tr("WhatsApp waitlist template")} hint=${tr("Body variables {{1}} name, {{2}} service, {{3}} time. Needed when the customer hasn't written in 24h.")}><input ...${bind("waitlist_template")} /></${Field}>

    <h2 id="automation">${tr("Automated message guardrails")}</h2>
    <p class="muted small">${tr("Apply to everything the platform sends on its own. Replies to customers who write in are never held.")}</p>
    <label class="toggle"><input type="checkbox" ...${check("outbound_paused")} /> ${tr("Pause all automated messages")}</label>
    ${values.outbound_paused && html`<div class="callout warn small">${tr("Paused: no reminders, follow-ups, review requests, rebook nudges or waitlist offers go out. Reminders due while paused are dropped; the others wait.")}</div>`}
    <div class="row">
      <${Field} label=${tr("Quiet hours from")} hint=${tr("Follow-ups, review requests, rebook nudges and waitlist offers wait until quiet hours end. Reminders still go out.")}><select ...${bind("quiet_start_hour")}>${HOURS}</select></${Field}>
      <${Field} label=${tr("Quiet hours until")}><select ...${bind("quiet_end_hour")}>${HOURS}</select></${Field}>
      <${Field} label=${tr("Daily limit")} hint=${tr("Most automated (non-reminder) messages per day.")}><input type="number" min="0" max="5000" ...${bind("daily_send_cap")} /></${Field}>
    </div>
    <label class="toggle"><input type="checkbox" ...${check("quiet_on_closed_days")} /> ${tr("Hold them on days the business is closed (Shabbat, holidays, closed dates)")}</label>
    <div class="form-actions"><button class="btn primary" disabled=${busy}>${tr("Save agent")}</button></div>
  </form>`;
}

const fmtDay = (iso) => new Intl.DateTimeFormat(locale(), { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso));
const usd = (n) => `$${Number(n).toFixed(2)}`;

/**
 * Why no Anthropic key can pay for a call, in the owner's language. The API sends the same
 * reasons as a `block` code plus an English sentence; the dashboard renders its own so Hebrew
 * stays Hebrew. Keys match AiKeyBlock in src/agent/credentials.ts.
 */
export const AI_KEY_BLOCK_TEXT = {
  trial_credit_spent: "Your trial's AI credit is used up, so the agent stopped answering and chats go to your team. Add your own Anthropic API key, or choose a plan that includes AI usage.",
  own_key_required: "Your plan doesn't include AI usage, so the agent stopped answering and chats go to your team. Add your own Anthropic API key, or move to Growth or Pro.",
  subscription_inactive: "AI usage isn't covered by your subscription right now.",
  platform_key_missing: "Add your business's Anthropic API key to switch the agent on.",
};

/** What is wrong with a saved key, in the owner's language. Keys match KeyProblem in src/agent/credentials.ts. */
export const KEY_PROBLEM_TEXT = {
  key_no_credit: "Your Anthropic account is out of credit, so the agent can't answer and chats go to your team. Add credit at console.anthropic.com → Billing; the agent picks up again on the next message.",
  key_rejected: "Anthropic rejected your API key, so the agent can't answer and chats go to your team. The key may have been revoked: create a new one at console.anthropic.com → API keys and replace it here.",
};

/** Creating a key, step by step, for an owner who has never opened the Anthropic console. */
function KeySteps() {
  return html`<details class="key-steps" open>
    <summary>${tr("How to get a key (about 5 minutes)")}</summary>
    <ol class="small">
      <li>${tr("Sign up at")} <a href="https://console.anthropic.com" target="_blank" rel="noopener" dir="ltr">console.anthropic.com</a> ${tr("with your business email.")}</li>
      <li>${tr("Billing → add credit. $10 to $20 is a good start for a small business; Anthropic charges only for what the agent uses.")}</li>
      <li>${tr("Settings → Limits → set a monthly spend limit, so a busy month can never surprise you.")}</li>
      <li>${tr("API keys → Create key, name it RELAYZA, copy it and paste it below.")}</li>
    </ol>
  </details>`;
}

/**
 * Whose Anthropic key the agent runs on. Our key carries a business through the trial and keeps
 * paying on the plans whose price includes AI usage; anywhere else the business adds its own key
 * here and Anthropic bills them directly. Owner only: a key is a payment instrument.
 */
function AiKey({ t }) {
  const [state, reload] = useLoad(() => get(`${t.base}/ai-key`), [t.id]);
  const [key, setKey] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, run] = useAction();
  if (!state) return null;
  const isOwner = t.role === "owner";
  const credit = state.trial_credit;
  const pct = credit ? Math.min(100, Math.round((credit.spent / credit.cap) * 100)) : 0;

  const save = async (e) => {
    e.preventDefault();
    await run(() => put(`${t.base}/ai-key`, { api_key: key.trim() }), tr("Your Anthropic key is in use"));
    setKey("");
    setOpen(false);
    reload();
    t.reload();
  };
  const test = async () => {
    const r = await run(() => post(`${t.base}/ai-key/test`));
    if (r.result.ok) toast(tr("Your key works: the agent can answer on it"));
    else toast(tr(KEY_PROBLEM_TEXT[r.result.problem] ?? r.result.message), "error");
    reload();
    t.reload();
  };
  const remove = async () => {
    if (!confirm(tr("Remove your Anthropic key? The agent goes back to whatever your plan and trial cover, which may mean it stops answering."))) return;
    await run(() => del(`${t.base}/ai-key`), tr("Key removed"));
    reload();
    t.reload();
  };

  return html`<section class="card">
    <div class="section-head"><h2>${tr("Claude API key")}</h2>
      ${state.own_key
        ? html`<${Badge} kind="ok">${tr("Your own key")}</${Badge}>`
        : state.block
          ? html`<${Badge} kind="warn">${tr("No key")}</${Badge}>`
          : html`<${Badge} kind="accent">${state.source === "platform_trial" ? tr("Trial credit") : tr("Included in your plan")}</${Badge}>`}
    </div>

    ${state.block && html`<div class="callout warn small">${tr(AI_KEY_BLOCK_TEXT[state.block] ?? state.message)}</div>`}
    ${state.own_key?.problem && html`<div class="callout warn small">${tr(KEY_PROBLEM_TEXT[state.own_key.problem])} <span class="muted">${tr("Since {date}.", { date: fmtDay(state.own_key.problem_since) })}</span></div>`}

    ${state.own_key
      ? html`<p class="small">${tr("The agent runs on your own Anthropic key (ending {hint}), added {date}. Anthropic bills you directly for what it uses, and our plan price covers the platform only.", { hint: state.own_key.hint, date: fmtDay(state.own_key.added_at) })}
          ${state.own_key.last_verified_at && html` <span class="muted">${tr("Last checked {date}.", { date: fmtDay(state.own_key.last_verified_at) })}</span>`}</p>`
      : credit
        ? html`<p class="small">${tr("While you're on trial the agent runs on our Anthropic key, up to {cap} of AI usage for the whole trial.", { cap: usd(credit.cap) })}</p>
            <div class="usage">
              <div class="section-head small"><span>${tr("Trial AI credit used")}</span><strong dir="ltr">${usd(credit.spent)} / ${usd(credit.cap)}</strong></div>
              <div class="meter" role="progressbar" aria-valuenow=${pct} aria-valuemin="0" aria-valuemax="100"><div class=${pct >= 100 ? "over" : pct >= 80 ? "near" : ""} style=${`width:${pct}%`}></div></div>
              <p class="muted small">${state.plan_includes_ai
                ? tr("When the trial ends, AI usage is included in your plan.")
                : tr("Your plan doesn't include AI usage, so add your own key before the trial ends to keep the agent answering.")}</p>
            </div>`
        : state.plan_includes_ai
          ? html`<p class="small">${tr("AI usage is included in your plan: the agent runs on our Anthropic key and there is nothing to set up. You can still put your own key in and be billed by Anthropic directly.")}</p>`
          : html`<p class="small">${tr("Your plan doesn't include AI usage. Add your own Anthropic API key and the agent runs on it, billed to you by Anthropic.")}</p>`}

    ${!isOwner
      ? html`<p class="muted small">${tr("Only the owner can change the key.")}</p>`
      : html`${open || (!state.own_key && state.block)
          ? html`<form onSubmit=${save}>
              ${!state.own_key && html`<${KeySteps} />`}
              <${Field} label=${tr("Anthropic API key")} hint=${tr("Create one at console.anthropic.com → API keys, after adding credit there. We store it encrypted and never show it again.")}>
                <input required type="password" dir="ltr" autocomplete="off" placeholder="sk-ant-…" value=${key} onInput=${(e) => setKey(e.target.value)} />
              </${Field}>
              <div class="form-actions">
                <button class="btn primary" disabled=${busy || key.trim().length < 20}>${busy ? tr("Checking…") : tr("Save key")}</button>
                ${open && html`<button type="button" class="btn ghost" onClick=${() => { setOpen(false); setKey(""); }}>${tr("Cancel")}</button>`}
              </div>
            </form>`
          : html`<div class="form-actions">
              ${state.own_key && html`<button class="btn" disabled=${busy} onClick=${test}>${busy ? tr("Testing…") : tr("Test key")}</button>`}
              <button class="btn" onClick=${() => setOpen(true)}>${state.own_key ? tr("Replace key") : tr("Add my own key")}</button>
              ${state.own_key && html`<button class="btn ghost danger" disabled=${busy} onClick=${remove}>${tr("Remove key")}</button>`}
            </div>`}`}
  </section>`;
}

/** Weekly QA: Claude's suggestions from chats that needed a person or were lost. Nothing applies until accepted. */
function WeeklyReview({ t, onApplied }) {
  const [data, reload] = useLoad(() => get(`${t.base}/qa`), [t.id]);
  const [drafts, setDrafts] = useState({});
  const [busy, run] = useAction();
  useEffect(() => {
    if (data && location.hash === "#weekly-review") document.getElementById("weekly-review")?.scrollIntoView({ block: "start" });
  }, [!!data]);
  if (!data) return null;
  const { report, suggestions } = data;
  const act = async (s, action) => {
    const d = drafts[s.id] ?? {};
    await run(() => post(`${t.base}/qa/suggestions/${s.id}/${action}`, action === "accept" ? { title: d.title ?? s.title, content: d.content ?? s.content } : {}), action === "accept" ? (s.kind === "knowledge" ? tr("Added to the knowledge base") : tr("Added to the instructions")) : tr("Dismissed"));
    reload();
    if (action === "accept") onApplied();
  };
  const runNow = async () => {
    await run(() => post(`${t.base}/qa/run`), tr("Review finished"));
    reload();
  };
  return html`<section class="card" id="weekly-review">
    <div class="section-head"><h2>${tr("Weekly review")}</h2>${t.role !== "agent" && html`<button class="btn small ghost" disabled=${busy} onClick=${runNow}>${busy ? tr("Reviewing…") : tr("Run review now")}</button>`}</div>
    <p class="muted small">${tr("Every week the AI reads the chats that needed a person or ended as lost leads, and suggests what to add so it handles them next time.")}</p>
    ${report && html`<p class="small">${report.summary}</p>
      ${report.lost_reasons?.length > 0 && html`<div class="chips">${report.lost_reasons.map((r) => html`<span class="chip">${r.reason} <span class="count">${r.count}</span></span>`)}</div>`}
      <p class="muted small">${tr("{n} conversations reviewed", { n: report.conversations_reviewed })} · ${new Date(report.created_at).toLocaleDateString(locale())}</p>`}
    ${suggestions.map((s) => {
      // The server refuses Accept by the same rule (src/qa/routes.ts); a knowledge entry's title counts too.
      const unfilled = hasPlaceholder(drafts[s.id]?.content ?? s.content, s.kind === "knowledge" ? (drafts[s.id]?.title ?? s.title) : "");
      return html`<div class="suggestion">
      <p class="small"><${Badge} kind="accent">${s.kind === "knowledge" ? tr("Knowledge") : tr("Instruction")}</${Badge}> <span class="muted">${s.rationale}</span></p>
      ${s.kind === "knowledge" && html`<input dir="auto" value=${drafts[s.id]?.title ?? s.title} onInput=${(e) => setDrafts({ ...drafts, [s.id]: { ...drafts[s.id], title: e.target.value } })} />`}
      <textarea rows="4" dir="auto" value=${drafts[s.id]?.content ?? s.content} onInput=${(e) => setDrafts({ ...drafts, [s.id]: { ...drafts[s.id], content: e.target.value } })}></textarea>
      ${unfilled && html`<p class="muted small">${tr("Fill in the [bracketed] parts before accepting.")}</p>`}
      ${s.conversation_ids?.length > 0 && html`<p class="small">${tr("Source conversations")}: ${s.conversation_ids.map((id, i) => html`<a href=${`/inbox/${id}`}>${tr("Conversation {n}", { n: i + 1 })} </a>`)}</p>`}
      ${t.role !== "agent" && html`<div class="row-actions">
        <button class="btn small primary" disabled=${busy || unfilled} onClick=${() => act(s, "accept")}>${tr("Accept")}</button>
        <button class="btn small ghost" disabled=${busy} onClick=${() => act(s, "dismiss")}>${tr("Dismiss")}</button>
      </div>`}
    </div>`;
    })}
  </section>`;
}

function Knowledge({ t, tick }) {
  const [data, reload] = useLoad(() => get(`${t.base}/knowledge`), [t.id, tick]);
  const [edit, setEdit] = useState(null);
  const [busy, run] = useAction();
  const save = async (e) => {
    e.preventDefault();
    const body = { title: edit.title, content: edit.content };
    await run(() => (edit.id ? put(`${t.base}/knowledge/${edit.id}`, body) : post(`${t.base}/knowledge`, body)), tr("Saved"));
    setEdit(null);
    reload();
  };
  return html`<section class="card">
    <div class="section-head"><h2>${tr("Knowledge base")}</h2><button class="btn" onClick=${() => setEdit({ title: "", content: "" })}>${tr("Add entry")}</button></div>
    <p class="muted small">${tr("FAQs, prices, parking, preparation instructions, policies. The agent answers only from what is here and hands off otherwise.")}</p>
    ${!data && html`<${Loading} />`}
    <ul class="kb">${data?.items.map((k) => html`<li>
      <div><strong>${k.title}</strong>${k.has_placeholders && html` <${Badge} kind="warn">${tr("[ ] to fill in")}</${Badge}>`}<div class="muted small clamp" dir="auto">${k.content}</div></div>
      <div class="row-actions"><button class="btn small" onClick=${() => setEdit(k)}>${tr("Edit")}</button>
        <button class="btn small ghost" onClick=${async () => { await run(() => del(`${t.base}/knowledge/${k.id}`)); reload(); }}>${tr("Delete")}</button></div>
    </li>`)}</ul>
    ${edit && html`<${Modal} title=${edit.id ? tr("Edit entry") : tr("New entry")} onClose=${() => setEdit(null)}>
      <form onSubmit=${save}>
        <${Field} label=${tr("Title")}><input required dir="auto" value=${edit.title} onInput=${(e) => setEdit({ ...edit, title: e.target.value })} /></${Field}>
        <${Field} label=${tr("Content")}><textarea required rows="8" dir="auto" value=${edit.content} onInput=${(e) => setEdit({ ...edit, content: e.target.value })}></textarea></${Field}>
        <button class="btn primary full" disabled=${busy}>${tr("Save")}</button>
      </form>
    </${Modal}>`}
  </section>`;
}

/**
 * Documents and links: the material the agent reads wholesale, instead of the owner retyping
 * their price list as FAQ entries. Uploads are converted to text here and the text is what the
 * agent sees, so the owner can read and correct it.
 */
function Documents({ t }) {
  const [data, reload] = useLoad(() => get(`${t.base}/knowledge/documents`), [t.id]);
  const [edit, setEdit] = useState(null);
  const [link, setLink] = useState(null);
  const [busy, run] = useAction();
  const readOnly = t.role === "agent";
  const limits = data?.limits;

  const upload = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    for (const file of files) {
      if (limits && file.size > limits.max_file_bytes) {
        toast(tr("{name} is larger than {max}.", { name: file.name, max: fmtBytes(limits.max_file_bytes) }), "error");
        continue;
      }
      // One unreadable file shouldn't stop the rest of the selection.
      try {
        await run(async () => {
          const base64 = await readFileBase64(file);
          const doc = await post(`${t.base}/knowledge/documents`, { filename: file.name, mime: file.type || "", data: base64 });
          if (doc.status === "failed") toast(doc.error || tr("We couldn't read that file."), "error");
          else toast(tr("{name} added: the agent reads {n} characters.", { name: doc.title, n: doc.chars.toLocaleString(locale()) }));
          return doc;
        });
      } catch {
        /* the toast already said what went wrong */
      }
      reload();
    }
  };

  const saveLink = async (e) => {
    e.preventDefault();
    let doc;
    try {
      doc = await run(() => post(`${t.base}/knowledge/documents/link`, { url: link.url, title: link.title, auto_refresh: link.auto_refresh }));
    } catch {
      return;
    }
    setLink(null);
    reload();
    if (doc.status === "failed") toast(doc.error, "error");
    else toast(tr("{name} added: the agent reads {n} characters.", { name: doc.title, n: doc.chars.toLocaleString(locale()) }));
  };

  const openEditor = async (doc) => {
    try {
      setEdit(await run(() => get(`${t.base}/knowledge/documents/${doc.id}`)));
    } catch {
      /* the toast already said what went wrong */
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    await run(() => patch(`${t.base}/knowledge/documents/${edit.id}`, { title: edit.title, text: edit.text, active: edit.active, auto_refresh: edit.auto_refresh }), tr("Saved"));
    setEdit(null);
    reload();
  };

  return html`<section class="card">
    <div class="section-head"><h2>${tr("Documents & links")}</h2>
      ${!readOnly && html`<div class="row-actions">
        <label class="btn small">${tr("Upload document")}<input type="file" multiple accept=${limits?.accepted ?? ""} style="display:none" disabled=${busy} onChange=${upload} /></label>
        <button class="btn small" disabled=${busy} onClick=${() => setLink({ url: "", title: "", auto_refresh: true })}>${tr("Add a link")}</button>
      </div>`}
    </div>
    <p class="muted small">${tr("Price lists, treatment guides, policies, and pages of your own website. We read the text out of each one and the agent answers from it. Links are re-read every week, so a change on your site reaches the agent on its own.")}</p>
    ${!data && html`<${Loading} />`}
    ${data && !data.documents.length && html`<${Empty} title=${tr("Nothing uploaded yet")}>${tr("Upload the document you send new customers, or add your website, and the agent stops guessing.")}</${Empty}>`}
    ${data?.documents.length > 0 && html`<ul class="kb">${data.documents.map((d) => html`<li>
      <div>
        <strong dir="auto">${d.title}</strong>
        ${!d.active && html` <${Badge} kind="muted">${tr("not in use")}</${Badge}>`}
        ${d.status === "failed" && html` <${Badge} kind="warn">${tr("couldn't read")}</${Badge}>`}
        ${d.truncated && html` <${Badge} kind="muted">${tr("shortened")}</${Badge}>`}
        <div class="muted small">
          ${d.kind === "link" ? html`<span dir="ltr">${d.url}</span>` : `${d.filename} · ${fmtBytes(d.bytes)}`}
          ${d.status === "ready" ? ` · ${tr("{n} characters", { n: d.chars.toLocaleString(locale()) })}` : ""}
          ${d.kind === "link" && d.refreshed_at ? ` · ${tr("read {date}", { date: new Date(d.refreshed_at).toLocaleDateString(locale()) })}` : ""}
        </div>
        ${d.status === "failed" && html`<div class="muted small">${d.error}</div>`}
      </div>
      ${!readOnly && html`<div class="row-actions">
        ${d.kind === "link" && html`<button class="btn small ghost" disabled=${busy} onClick=${async () => { const r = await run(() => post(`${t.base}/knowledge/documents/${d.id}/refresh`), tr("Re-read")); reload(); if (r?.status === "failed") toast(r.error, "error"); }}>${tr("Re-read")}</button>`}
        <button class="btn small" disabled=${busy} onClick=${() => openEditor(d)}>${tr("Edit")}</button>
        <button class="btn small ghost" disabled=${busy} onClick=${async () => { await run(() => del(`${t.base}/knowledge/documents/${d.id}`), tr("Removed")); reload(); }}>${tr("Delete")}</button>
      </div>`}
    </li>`)}</ul>`}
    ${data?.usage.over_budget && html`<div class="callout warn small">${tr("That is more than the agent can hold at once ({max} characters). The oldest documents are read first; turn off what the agent doesn't need.", { max: data.limits.prompt_chars.toLocaleString(locale()) })}</div>`}

    ${link && html`<${Modal} title=${tr("Add a link")} onClose=${() => setLink(null)}>
      <form onSubmit=${saveLink}>
        <${Field} label=${tr("Page address")} hint=${tr("Your website, price page, menu or FAQ. We also read the pages it links to, like services and prices.")}>
          <input required type="url" dir="ltr" placeholder="https://" value=${link.url} onInput=${(e) => setLink({ ...link, url: e.target.value })} />
        </${Field}>
        <${Field} label=${tr("Name (optional)")}><input dir="auto" value=${link.title} onInput=${(e) => setLink({ ...link, title: e.target.value })} /></${Field}>
        <label class="toggle"><input type="checkbox" checked=${link.auto_refresh} onChange=${(e) => setLink({ ...link, auto_refresh: e.target.checked })} /> ${tr("Re-read this page every week")}</label>
        <button class="btn primary full" disabled=${busy}>${busy ? tr("Reading…") : tr("Add")}</button>
      </form>
    </${Modal}>`}

    ${edit && html`<${Modal} title=${tr("Edit document")} onClose=${() => setEdit(null)}>
      <form onSubmit=${saveEdit}>
        <${Field} label=${tr("Name")}><input required dir="auto" value=${edit.title} onInput=${(e) => setEdit({ ...edit, title: e.target.value })} /></${Field}>
        <${Field} label=${tr("What the agent reads")} hint=${tr("Exactly this text goes to the agent. Correct anything the conversion got wrong, and delete what customers never ask about.")}>
          <textarea rows="14" dir="auto" value=${edit.text} onInput=${(e) => setEdit({ ...edit, text: e.target.value })}></textarea>
        </${Field}>
        <label class="toggle"><input type="checkbox" checked=${edit.active} onChange=${(e) => setEdit({ ...edit, active: e.target.checked })} /> ${tr("The agent uses this")}</label>
        ${edit.kind === "link" && html`<label class="toggle"><input type="checkbox" checked=${edit.auto_refresh} onChange=${(e) => setEdit({ ...edit, auto_refresh: e.target.checked })} /> ${tr("Re-read this page every week")}</label>`}
        ${edit.kind === "link" && edit.auto_refresh && html`<p class="muted small">${tr("Your edits here are replaced the next time the page is read.")}</p>`}
        <button class="btn primary full" disabled=${busy}>${tr("Save")}</button>
      </form>
    </${Modal}>`}
  </section>`;
}

const PLAYGROUND_KEY = "mz.playground";
/**
 * The test chat's visitor id, kept per business. The server signs it, which is what lets the test
 * chat answer directly when replies are held for approval; an older unsigned id is replaced.
 */
async function playgroundVisitor(t, reset = false) {
  const storeKey = `${PLAYGROUND_KEY}.${t.id}`;
  let id = null;
  try {
    id = reset ? null : localStorage.getItem(storeKey);
  } catch {
    /* ignore */
  }
  if (!id || !/^v_test_[0-9a-f]{16}_[0-9a-f]{24}$/.test(id)) {
    id = (await post(`${t.base}/test-chat/visitor`)).visitor_id;
    try {
      localStorage.setItem(storeKey, id);
    } catch {
      /* ignore */
    }
  }
  return id;
}

/**
 * Talks to the agent through the real website-chat channel, exactly as a customer would. The
 * suggested first message names the business's most booked active service of the last 90 days (the
 * first by name when nothing was booked), so a salon is not handed a dental question.
 */
function TestChat({ t }) {
  const [channels] = useLoad(() => get(`${t.base}/channels`), [t.id]);
  const [services] = useLoad(() => get(`${t.base}/services`).catch(() => null), [t.id]);
  const service = services?.services
    .filter((s) => s.active)
    .reduce((top, s) => (!top || s.recent_bookings > top.recent_bookings ? s : top), null)?.name;
  const example = service
    ? tr("Try: \"Hi, how much is {service}, and is there a slot tomorrow morning?\"", { service })
    : tr("Try: \"Hi, what do you offer, and is there a slot tomorrow morning?\"");
  const key = channels?.channels.find((c) => c.type === "webchat")?.external_id;
  const [visitor, setVisitor] = useState(null);
  useEffect(() => {
    playgroundVisitor(t).then(setVisitor).catch(() => {});
  }, [t.id]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [waiting, setWaiting] = useState(false);
  const bottom = useRef(null);
  // Test bookings still holding a slot (#182). Counted again whenever the chat moves on, since the
  // agent books from it; they also clear themselves once their time passes.
  const [tests, reloadTests] = useLoad(() => get(`${t.base}/test-chat/bookings`).catch(() => null), [t.id, messages.length]);
  const [clearing, runClear] = useAction();
  const clearTests = async () => {
    await runClear(() => post(`${t.base}/test-chat/bookings/clear`), tr("Test bookings cleared"));
    reloadTests();
  };

  useEffect(() => {
    if (!key || !visitor) return;
    let alive = true;
    const poll = async () => {
      const res = await fetch(`/api/public/webchat/${key}/messages?visitor_id=${visitor}`).then((r) => r.json()).catch(() => null);
      if (!alive || !res) return;
      setMessages(res.messages);
      const last = res.messages[res.messages.length - 1];
      setWaiting(last?.direction === "in");
    };
    poll();
    const id = setInterval(poll, 1500);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [key, visitor]);
  useEffect(() => bottom.current?.scrollIntoView({ block: "end" }), [messages.length, waiting]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !key || !visitor) return;
    const body = text;
    setText("");
    setMessages((m) => [...m, { id: `local${Date.now()}`, direction: "in", body }]);
    setWaiting(true);
    await fetch(`/api/public/webchat/${key}/messages`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ visitor_id: visitor, text: body, name: "Test customer" }) });
  };

  return html`<section class="card test-chat">
    <div class="section-head"><h2>${tr("Test your agent")}</h2><button class="btn small ghost" onClick=${() => { playgroundVisitor(t, true).then(setVisitor).catch(() => {}); setMessages([]); }}>${tr("New chat")}</button></div>
    <p class="muted small">${tr("Bookings made here are test bookings: they hold the time on your Bookings page, but don't reach your calendar, send reminders or webhooks, or count in your reports. They're released by themselves once their time passes. Save changes before testing.")}</p>
    ${tests?.live > 0 && html`<div class="row-actions"><button class="btn small ghost" disabled=${clearing} onClick=${clearTests}>${tr("Clear test bookings ({n})", { n: tests.live })}</button></div>`}
    <div class="phone">
      <div class="messages">
        ${!messages.length && html`<p class="muted small center">${example}</p>`}
        ${messages.map((m) => html`<div class="msg ${m.direction === "in" ? "out" : "in"}"><div class="bubble" dir="auto">${m.body}</div></div>`)}
        ${waiting && html`<div class="msg in"><div class="bubble typing"><span></span><span></span><span></span></div></div>`}
        <div ref=${bottom}></div>
      </div>
      <form class="composer" onSubmit=${send}>
        <input dir="auto" placeholder=${tr("Message")} value=${text} onInput=${(e) => setText(e.target.value)} />
        <button class="btn primary" disabled=${!text.trim() || !key || !visitor}>${tr("Send")}</button>
      </form>
    </div>
  </section>`;
}

